// Study/Flashcards/RatingFeedbackBubble.tsx
//
// The "+10min" bubble that pops once a card is rated, shared by the full
// flashcard screen and the in-chat study-set deck (ORA-833) so the two cannot
// drift: same markup, same wording, same animation. What each surface asks for
// is only what it already did — the chat deck takes the compact size, because
// the full-size bubble read as disproportionate in a shrunk UI, and hides the
// bubble from the screen reader as its old layer did.
//
// The bubble centres itself on the top of the nearest positioned ancestor, so
// the caller decides what it is centred over. Both surfaces give it the whole
// rating row: the full screen's `.flashcard_actions`, the deck's controls
// wrapper.
//
// Mount it with `key={feedback.key}`: the pop is a CSS animation that plays on
// mount, so two ratings in a row must replace the element rather than update it.

import { cn } from '@/lib/utils';
import type { RatingFeedback } from '@/hooks/useFlashcardResponse';

export interface RatingFeedbackBubbleProps {
  feedback: RatingFeedback;
  /** `compact` is the in-chat size: smaller type, padding, radius and rise. */
  size?: 'default' | 'compact';
  /**
   * Whether the bubble is kept out of the accessibility tree. Each surface asks
   * for what it already did before this component existed (ORA-833): the chat
   * deck hid its bubble behind an `aria-hidden` layer, the full flashcard screen
   * never hid it.
   */
  isDecorative?: boolean;
  /** For the surfaces whose tests read the bubble out of a larger block. */
  testId?: string;
}

const RatingFeedbackBubble = ({
  feedback,
  size = 'default',
  isDecorative = false,
  testId,
}: RatingFeedbackBubbleProps) => (
  <div
    aria-hidden={isDecorative ? 'true' : undefined}
    className={cn(
      'rating-feedback',
      feedback.isPositive ? 'right' : 'wrong',
      size === 'compact' && 'rating-feedback--compact'
    )}
    data-testid={testId}
  >
    {feedback.label}
  </div>
);

export default RatingFeedbackBubble;
